import { Table } from '@tanstack/react-table';
import React, { FC, useEffect, useState } from 'react';
import { IDocumentSearchResources } from '../modules/document-search/document-search.props.autogenerated';
import { ILxsBillingDocumentsResources } from '../modules/lxs-billing-documents/lxs-billing-documents.props.autogenerated';
import { ILxsCreateOrderResources } from '../modules/lxs-create-order/lxs-create-order.props.autogenerated';
import { IOrderHistoryResources } from '../modules/order-history';

interface PaginationProps<T> {
    pageIndex: number;
    resources: ILxsCreateOrderResources | IOrderHistoryResources | IDocumentSearchResources | ILxsBillingDocumentsResources;
    table: Table<T>;
    pageSizeOptions?: number[];
    componentName?: string;
}

export const Pagination = <T,>({ pageIndex, resources, table, pageSizeOptions, componentName }: PaginationProps<T>) => {
    const pageCount = table.getPageCount();
    const [inputValue, setInputValue] = useState(String(pageIndex + 1));

    useEffect(() => {
        setInputValue(String(pageIndex + 1));
    }, [pageIndex]);

    useEffect(() => {
        if (componentName && componentName !== null) {
            const storedSize = localStorage.getItem(componentName);
            if (storedSize) {
                table.setPageSize(Number(storedSize));
            }
        }
    }, [componentName, table]);

    const clampPage = (value: number) => Math.max(1, Math.min(value, pageCount));

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        if (value === '') {
            setInputValue('');
        } else {
            const page = clampPage(Number(value));
            setInputValue(String(page));
        }
    };

    const handleInputCommit = () => {
        if (inputValue === '') {
            setInputValue(String(pageIndex + 1));
            return;
        }
        const page = clampPage(Number(inputValue));
        const newPageIndex = page - 1;

        table.setPageIndex(newPageIndex);
    };

    const handlePageChange = (newPageIndex: number) => {
        const clampedIndex = Math.max(0, Math.min(newPageIndex, pageCount - 1));

        table.setPageIndex(clampedIndex);
    };

    const PageSizeSelector: FC<{
        className: string;
        pageSizeOptions: number[];
        componentName?: string;
    }> = ({ pageSizeOptions, className, componentName }) => {
        const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
            const newPageSize = Number(e.target.value);
            if (componentName) {
                localStorage.setItem(componentName, newPageSize.toString());
            }
            table.setPageSize(newPageSize);
        };

        return (
            <div className={className}>
                <label className='msc-pagination__page-limit-size-selector-label' htmlFor='page-size-select'>
                    {resources.paginationPageSizeSelectorLabel}
                </label>
                <select
                    className='msc-pagination__page-limit-size-selector-select'
                    id='page-size-select'
                    value={table.getState().pagination.pageSize}
                    onChange={handleChange}
                >
                    {pageSizeOptions.map((size: number) => (
                        <option key={size} value={size}>
                            {size}
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    return (
        <div className='msc-pagination__wrapper'>
            {table.getPageCount() !== 1 && (
                <div className='msc-pagination__nav'>
                    <PaginationButton
                        className='msc-pagination__button--is-first'
                        onClick={() => handlePageChange(0)}
                        disabled={pageIndex === 0}
                        title={resources.createOrderFirstPageButtonTitle}
                        icon='double-chevron-left'
                    />
                    <PaginationButton
                        className='msc-pagination__button--is-previous'
                        onClick={() => handlePageChange(pageIndex - 1)}
                        disabled={pageIndex === 0}
                        title={resources.createOrderPreviousPageButtonTitle}
                    />
                    <span className='msc-pagination__page-count'>
                        <input
                            type='number'
                            className='msc-pagination__input'
                            value={inputValue}
                            onChange={handleInputChange}
                            onKeyDown={e => e.key === 'Enter' && handleInputCommit()}
                            onBlur={handleInputCommit}
                            max={pageCount}
                            min={1}
                        />
                        / {pageCount}
                    </span>
                    <PaginationButton
                        className='msc-pagination__button--is-next'
                        onClick={() => handlePageChange(pageIndex + 1)}
                        disabled={pageIndex >= pageCount - 1}
                        title={resources.createOrderNextPageButtonTitle}
                    />
                    <PaginationButton
                        className='msc-pagination__button--is-last'
                        onClick={() => handlePageChange(pageCount - 1)}
                        disabled={pageIndex >= pageCount - 1}
                        title={resources.createOrderLastPageButtonTitle}
                        icon='double-chevron-right'
                    />
                </div>
            )}
            {pageSizeOptions && table.getFilteredRowModel().rows.length >= Math.min(...pageSizeOptions) && (
                <div className='msc-pagination__page-limit'>
                    <PageSizeSelector
                        className='msc-pagination__page-limit-size-selector'
                        pageSizeOptions={pageSizeOptions}
                        componentName={componentName}
                    />
                </div>
            )}
        </div>
    );
};

const PaginationButton: FC<{
    className: string;
    disabled: boolean;
    title: string;
    icon?: string;
    onClick: () => void;
}> = ({ className, onClick, disabled, title, icon }) => (
    <button className={`msc-pagination__button ${className}`} onClick={onClick} disabled={disabled} title={title}>
        {icon && <span className={icon}></span>}
    </button>
);
